if (!customElements.get('vlx-frame-fit')) {
  customElements.define('vlx-frame-fit', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || typeof HTMLDialogElement === 'undefined') return;

      this.details = this.querySelector('.vlx-frame-measurements__details');
      this.trigger = this.details?.querySelector('summary');
      this.panel = this.querySelector('[data-fit-panel]');
      if (!this.trigger || !this.panel) return;
      this.productName = this.dataset.productName || 'Diese Fassung';

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const title = this.panel.querySelector('h2');
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'vlx-frame-measurements__dialog';
      this.dialog.id = `${title.id}-dialog`;
      this.dialog.setAttribute('aria-labelledby', title.id);
      this.dialog.append(this.panel);
      document.body.append(this.dialog);

      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.details.open = false;
      this.panel.querySelectorAll('[data-fit-interactive]').forEach(element => { element.hidden = false; });

      this.trigger.addEventListener('click', event => {
        event.preventDefault();
        this.open();
      }, options);
      this.dialog.querySelector('[data-fit-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('vlx-fit-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.restoreFocusOnClose) this.restoreFocus();
      }, options);
      this.dialog.addEventListener('pointerdown', event => {
        this.backdropPressed = event.target === this.dialog;
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog && this.backdropPressed) this.close();
        this.backdropPressed = false;
      }, options);
      this.dialog.querySelector('[data-fit-tryon]').addEventListener('click', () => this.openTryOn(), options);
      this.inputs = [...this.dialog.querySelectorAll('[data-fit-input]')];
      this.inputs.forEach(input => input.addEventListener('input', () => this.updateComparison(), options));
      this.band = this.dialog.querySelector('[data-fit-band]');
      this.dialog.querySelector('[data-fit-band-tryon]')?.addEventListener('click', () => this.openTryOn(), options);
      this.dialog.querySelector('[data-fit-reset]')?.addEventListener('click', () => {
        this.inputs.forEach(input => { input.value = ''; });
        this.updateComparison();
        this.inputs[0]?.focus();
      }, options);
      this.faqs = new Set();
      this.connectFaqs();
      document.addEventListener('shopify:section:load', () => this.connectFaqs(), options);
    }

    connectFaqs() {
      document.querySelectorAll('[data-frame-fit-faq]').forEach(faq => {
        const content = faq.querySelector('[data-fit-faq-content]');
        const fallback = faq.querySelector('[data-fit-faq-fallback]');
        const button = faq.querySelector('[data-fit-faq-open]');
        if (!content || !fallback || !button || this.faqs.has(faq)) return;
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', this.dialog.id);
        button.addEventListener('click', () => this.open(button), { signal: this.controller.signal });
        const measurements = content.querySelector('[data-fit-faq-measurements]');
        const noMeasurements = content.querySelector('[data-fit-faq-no-measurements]');
        if (measurements) measurements.hidden = this.inputs.length === 0;
        if (noMeasurements) noMeasurements.hidden = this.inputs.length > 0;
        content.hidden = false;
        fallback.hidden = true;
        this.faqs.add(faq);
      });
    }

    restoreFocus() {
      if (!this.isConnected) return;
      const target = this.returnFocusTarget?.isConnected ? this.returnFocusTarget : this.trigger;
      target.focus({ preventScroll: true });
    }

    open(opener = this.trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocusTarget = opener;
      this.restoreFocusOnClose = true;
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('vlx-fit-open');
      this.dialog.querySelector('[data-fit-close]').focus({ preventScroll: true });
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      document.documentElement.classList.remove('vlx-fit-open');
    }

    openTryOn() {
      const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const trigger = section?.querySelector('.glasson-btn-live');
      const component = document.querySelector('glasson-try-on');
      const status = this.dialog.querySelector('[data-fit-tryon-status]');

      if (!trigger || !component || typeof window.openTryOn !== 'function' || !customElements.get('glasson-try-on')) {
        status.textContent = 'Die virtuelle Anprobe lädt noch. Bitte versuche es gleich noch einmal.';
        status.hidden = false;
        return;
      }

      status.hidden = true;
      this.tryOnObserver?.disconnect();
      this.tryOnObserver = new MutationObserver(() => {
        const state = component.getAttribute('open');
        if (state === 'true') return;
        this.tryOnObserver.disconnect();
        this.tryOnFocusFrame = requestAnimationFrame(() => {
          this.restoreFocus();
        });
      });
      this.tryOnObserver.observe(component, { attributes: true, attributeFilter: ['open'] });
      this.restoreFocusOnClose = false;
      this.close();
      trigger.click();
    }

    updateComparison() {
      const messages = [];
      const format = number => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(number);
      const compared = {};
      this.inputs.forEach(input => {
        const row = input.closest('[data-fit-row]');
        const result = row.querySelector('[data-fit-result]');
        const raw = input.value.trim();
        const value = Number(raw.replace(',', '.'));
        const current = Number(input.dataset.frameValue);
        const valid = /^\d{1,3}(?:[.,]\d)?$/.test(raw) && Number.isFinite(value) && value > 0;
        row.removeAttribute('data-compared');
        row.removeAttribute('data-invalid');
        input.removeAttribute('aria-invalid');

        if (!raw) {
          result.textContent = result.dataset.emptyText;
          return;
        }
        if (!valid) {
          result.textContent = 'Bitte eine Millimeterzahl größer als 0 eintragen.';
          row.setAttribute('data-invalid', '');
          input.setAttribute('aria-invalid', 'true');
          messages.push(`${row.querySelector('label').textContent}: ${result.textContent}`);
          return;
        }

        const difference = Math.round((current - value) * 10) / 10;
        const amount = format(Math.abs(difference));
        const dimension = input.dataset.fitInput;
        compared[dimension] = { value, current };
        let text = '';
        if (dimension === 'lens') {
          text = difference === 0 ? 'Gleiche Glasbreite wie bei deiner Brille.' : `${this.productName} ist je Glas ${amount} mm ${difference < 0 ? 'schmaler' : 'breiter'}.`;
        } else if (dimension === 'bridge') {
          text = difference === 0 ? 'Gleicher Abstand zwischen den Gläsern.' : `${this.productName} hat ${amount} mm ${difference < 0 ? 'weniger' : 'mehr'} Abstand zwischen den Gläsern.`;
        } else {
          text = difference === 0 ? 'Gleiche Bügellänge wie bei deiner Brille.' : `Die Bügel von ${this.productName} sind ${amount} mm ${difference < 0 ? 'kürzer' : 'länger'}.`;
        }
        result.textContent = text;
        row.setAttribute('data-compared', '');
        messages.push(text);
      });

      const bandMessage = this.updateBand(compared, format);
      if (bandMessage) messages.push(bandMessage);
      this.dialog.querySelector('[data-fit-reset]').hidden = !this.inputs.some(input => input.value.length);
      clearTimeout(this.announcementTimer);
      this.announcementTimer = setTimeout(() => {
        const announcement = this.dialog?.querySelector('[data-fit-announcement]');
        if (announcement) announcement.textContent = messages.length ? messages.join(' ') : 'Eingaben zurückgesetzt.';
      }, 450);
    }

    updateBand({ lens, bridge }, format) {
      if (!this.band) return '';
      this.band.hidden = !lens;
      if (!lens) return '';

      // Common retailer guidance (Zenni, Mister Spex): total front width within ±2–3 mm and
      // bridge within ±2 mm of a well-fitting pair still fit comparably. Front width is estimated
      // as 2 × lens + bridge, so rim width is ignored; hence the upper 3 mm. Bridge set to 3 mm
      // by owner decision (2026-09-25), slightly more lenient than the ±2 mm guidance.
      // Up to 6 mm every single dimension can still be within its own tolerance (2 × 2 mm lens
      // + 2 mm bridge), so that range is shown as "slightly" instead of larger/smaller.
      const widthTolerance = 3;
      const softTolerance = 6;
      const bridgeTolerance = 3;
      const scale = 15;
      const round = number => Math.round(number * 10) / 10;
      const widthDifference = round(2 * (lens.current - lens.value) + (bridge ? bridge.current - bridge.value : 0));
      const bridgeDifference = bridge ? round(bridge.current - bridge.value) : 0;
      const bridgeOff = Math.abs(bridgeDifference) > bridgeTolerance;
      const distance = Math.abs(widthDifference);
      const bigger = widthDifference > 0;
      const state = distance <= widthTolerance ? 'match' : distance <= softTolerance ? 'slight' : 'clear';
      const clamped = Math.max(-scale, Math.min(scale, widthDifference));
      const amount = format(distance);
      const style = this.dataset.frameStyle;

      let verdict;
      let detail;
      if (state === 'match') {
        verdict = bridgeOff ? 'Ähnlich breit wie deine Brille' : 'Vergleichbar mit deiner Brille';
        detail = widthDifference === 0 ? 'Gleiche Breite wie deine Brille.' : `Nur rund ${amount} mm Unterschied in der Breite.`;
      } else if (state === 'slight') {
        verdict = `Etwas ${bigger ? 'größer' : 'kleiner'} als deine Brille`;
        detail = `Rund ${amount} mm ${bigger ? 'breiter' : 'schmaler'} über die Front. Das liegt noch im üblichen Spielraum.`;
      } else {
        verdict = `${this.productName} fällt ${bigger ? 'größer' : 'kleiner'} aus als deine Brille`;
        detail = `Über die ganze Front rund ${amount} mm ${bigger ? 'breiter' : 'schmaler'}.`;
      }
      if (style && bigger && state !== 'match') {
        detail += ` Als ${style}-Modell ist ${this.productName} bewusst größer geschnitten, das ist hier gewollt.`;
      }
      if (bridgeOff) {
        const wider = bridgeDifference > 0;
        detail += ` Der Steg ist ${format(Math.abs(bridgeDifference))} mm ${wider ? 'breiter' : 'schmaler'}, ${this.productName} sitzt dadurch eventuell ${wider ? 'tiefer' : 'höher'} auf der Nase.`;
      }
      const suggestTryOn = state === 'clear' || bridgeOff;
      if (suggestTryOn) detail += ' Wie sie an dir wirkt, zeigt dir die virtuelle Anprobe.';

      this.band.dataset.state = state;
      this.band.style.setProperty('--fit-band-zone', `${(widthTolerance / scale) * 100}%`);
      this.band.style.setProperty('--fit-band-soft', `${(softTolerance / scale) * 100}%`);
      this.band.style.setProperty('--fit-band-position', `${50 + (clamped / scale) * 50}%`);
      this.band.querySelector('[data-fit-band-verdict]').textContent = verdict;
      this.band.querySelector('[data-fit-band-detail]').textContent = detail;
      this.band.querySelector('[data-fit-band-tryon]').hidden = !suggestTryOn;
      return `${verdict}. ${detail}`;
    }

    disconnectedCallback() {
      clearTimeout(this.announcementTimer);
      cancelAnimationFrame(this.tryOnFocusFrame);
      this.tryOnObserver?.disconnect();
      this.controller?.abort();
      this.faqs?.forEach(faq => {
        const content = faq.querySelector('[data-fit-faq-content]');
        const fallback = faq.querySelector('[data-fit-faq-fallback]');
        if (content) content.hidden = true;
        if (fallback) fallback.hidden = false;
      });
      this.faqs?.clear();
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.classList.remove('vlx-fit-open');
      }
      if (this.panel && this.details) {
        this.panel.querySelectorAll('[data-fit-interactive]').forEach(element => { element.hidden = true; });
        this.details.append(this.panel);
      }
      this.dialog?.remove();
      this.trigger?.removeAttribute('aria-haspopup');
      this.trigger?.removeAttribute('aria-controls');
      this.trigger?.removeAttribute('aria-expanded');
      this.dialog = null;
      this.controller = null;
    }
  });
}
